// Pantalla de edición de la curva esperada (Módulo 3 — c3, ADR-029).
//
// Ruta: /ajustes/curva
//
// v0.5.1 (ADR-029): la curva ya no es por estirpe; es de la explotación
// por sexo. Solo hay un selector de sexo. La misma curva sirve para
// alimentar el gráfico de pesajes de cualquier camada del mismo sexo.
//
// El endpoint es GET/PUT /catalogos/curva-esperada?sexo=...

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useCurvaEsperada, useGuardarCurvaEsperada } from '@/lib/queries/catalogos';
import { useToast } from '@/components/toastStore';
import { extractApiMessage } from '@/lib/api';
import { useCurveEditor } from './curveEditor';
import { useAuthStore } from '@/stores/auth';

export function CurvaEstandarPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const role = useAuthStore((state) => state.user?.rol);
  const canWrite = role === 'admin' || role === 'ganadero';
  const canRead = canWrite || role === 'veterinario';
  const editor = useCurveEditor('peso');
  const { sexo, filas, hydrate, guardando } = editor;

  const curvaQ = useCurvaEsperada(sexo);
  const guardar = useGuardarCurvaEsperada(sexo);
  useEffect(() => {
    if (curvaQ.data) {
      hydrate(
        curvaQ.data.puntos.map((p) => ({
          edad: String(p.edad_dias),
          valor: String(p.peso_medio_kg).replace('.', ','),
        })),
      );
    }
  }, [sexo, curvaQ.data, hydrate]);

  const chartData = editor.points.map((p) => ({ edad: p.edad, peso: p.valor }));

  function actualizarFila(i: number, campo: 'edad' | 'valor', v: string) {
    if (!canWrite) return;
    editor.changeRows(filas.map((f, idx) => (idx === i ? { ...f, [campo]: v } : f)));
  }

  function añadirFila() {
    if (!canWrite) return;
    const ultima = filas[filas.length - 1];
    const edadBase = ultima ? Number(ultima.edad.replace(',', '.')) + 7 : 0;
    editor.changeRows([
      ...filas,
      { edad: String(Number.isFinite(edadBase) ? edadBase : 0), valor: '' },
    ]);
  }

  function eliminarFila(i: number) {
    if (!canWrite) return;
    editor.changeRows(filas.filter((_, idx) => idx !== i));
  }

  async function onGuardar() {
    if (!canWrite || curvaQ.isLoading || curvaQ.isError) return;
    const parsed = editor.startSave();
    if (!parsed) return;
    const puntos = parsed.map((p) => ({ edad_dias: p.edad, peso_medio_kg: p.valor }));
    try {
      const confirmed = await guardar.mutateAsync({ puntos });
      editor.saved(
        confirmed.puntos.map((p) => ({
          edad: String(p.edad_dias),
          valor: String(p.peso_medio_kg).replace('.', ','),
        })),
      );
      toast.show({ kind: 'success', message: t('pesajes.curva.guardada') });
      await curvaQ.refetch();
    } catch (err) {
      editor.failSave(err);
    } finally {
      editor.finishSave();
    }
  }

  if (!canRead)
    return (
      <main className="ui-page">
        <p role="alert">No tienes permiso para consultar esta curva.</p>
        {editor.dialog}
      </main>
    );

  if (curvaQ.isError) {
    return (
      <main className="mx-auto max-w-md px-4">
        <p role="alert">{extractApiMessage(curvaQ.error)}</p>
        {editor.dialog}
      </main>
    );
  }

  return (
    <main className="ui-page pb-8">
      <h1 className="ui-page-title mb-4">{t('pesajes.curva.titulo')}</h1>

      {/* Selector de sexo */}
      <div className="mb-4">
        <label htmlFor="curva-peso-sexo" className="mb-1 block text-sm font-medium">
          {t('pesajes.curva.sexo')}
        </label>
        <select
          id="curva-peso-sexo"
          value={sexo}
          disabled={guardando || curvaQ.isLoading}
          onChange={(e) => editor.changeSexo(e.target.value as 'machos' | 'hembras')}
          className="w-full rounded-lg border border-border bg-surface px-3 py-2"
        >
          <option value="machos">{t('pesajes.curva.machos')}</option>
          <option value="hembras">{t('pesajes.curva.hembras')}</option>
        </select>
      </div>

      {editor.errorSummary}
      {curvaQ.isLoading && <p role="status">{t('common.loading')}</p>}

      {/* Mini-gráfica en vivo */}
      {chartData.length > 1 ? (
        <div className="mb-4 rounded-xl border border-border bg-surface p-3">
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={chartData} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" />
              <XAxis
                dataKey="edad"
                type="number"
                domain={[0, 'dataMax']}
                tick={{ fontSize: 11, fill: 'var(--c-text-secondary)' }}
              />
              <YAxis tick={{ fontSize: 11, fill: 'var(--c-text-secondary)' }} width={42} />
              <Tooltip
                formatter={(value, _name, item) => {
                  const dataKey = (item as { dataKey?: string })?.dataKey;
                  if (dataKey === 'peso')
                    return [
                      `${Number(value).toFixed(3).replace('.', ',')} kg`,
                      t('pesajes.curva.peso'),
                    ];
                  return [String(value), ''];
                }}
                labelFormatter={(label) => `${label} ${t('pesajes.dias')}`}
                contentStyle={{
                  backgroundColor: 'var(--c-surface)',
                  border: '1px solid var(--c-border)',
                  borderRadius: '0.5rem',
                  color: 'var(--c-text-primary)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
                }}
                labelStyle={{
                  color: 'var(--c-text-secondary)',
                  fontSize: '0.75rem',
                  marginBottom: '0.25rem',
                }}
                itemStyle={{
                  color: 'var(--c-text-primary)',
                  fontSize: '0.8125rem',
                  padding: '1px 0',
                }}
                cursor={{ stroke: 'var(--c-border-strong)', strokeWidth: 1 }}
                wrapperStyle={{ outline: 'none' }}
              />
              <Line
                type="monotone"
                dataKey="peso"
                stroke="#378ADD"
                strokeWidth={2}
                dot={{ r: 2.5, fill: 'var(--c-surface)', stroke: '#378ADD', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="mb-4 text-sm text-muted-foreground">{t('pesajes.curva.graficaVacia')}</p>
      )}

      {/* Tabla editable */}
      <div className="mb-4 space-y-2">
        {filas.map((f, i) => (
          <div key={i} className="space-y-1">
            <div className="flex min-w-0 items-center gap-2">
              <label className="sr-only" htmlFor={editor.fieldProps(i, 'edad').id}>
                {t('pesajes.curva.edad')} {i + 1}
              </label>
              <input
                {...editor.fieldProps(i, 'edad')}
                disabled={!canWrite || guardando}
                type="text"
                inputMode="numeric"
                value={f.edad}
                onChange={(e) => actualizarFila(i, 'edad', e.target.value)}
                placeholder={t('pesajes.curva.edadPh')}
                aria-label={`${t('pesajes.curva.edad')} ${i + 1}`}
                className="min-h-12 w-20 shrink-0 rounded-lg border border-border bg-surface px-3 py-2 text-center"
              />
              <span className="text-sm text-muted-foreground">{t('pesajes.dias')}</span>
              <label className="sr-only" htmlFor={editor.fieldProps(i, 'valor').id}>
                {t('pesajes.curva.peso')} {i + 1}
              </label>
              <input
                {...editor.fieldProps(i, 'valor')}
                disabled={!canWrite || guardando}
                type="text"
                inputMode="decimal"
                value={f.valor}
                onChange={(e) => actualizarFila(i, 'valor', e.target.value)}
                placeholder={t('pesajes.curva.pesoPh')}
                aria-label={`${t('pesajes.curva.peso')} ${i + 1}`}
                className="min-h-12 min-w-0 flex-1 rounded-lg border border-border bg-surface px-3 py-2 text-center"
              />
              <span className="text-sm text-muted-foreground">kg</span>
              <button
                type="button"
                onClick={() => eliminarFila(i)}
                disabled={!canWrite || guardando}
                aria-label={t('pesajes.curva.eliminar')}
                className="flex size-12 shrink-0 items-center justify-center rounded-lg border border-border text-danger disabled:opacity-50"
              >
                ✕
              </button>
            </div>
            {(['edad', 'valor'] as const).map(
              (field) =>
                editor.errores[i]?.[field] && (
                  <p
                    key={field}
                    id={`${editor.fieldProps(i, field).id}-error`}
                    className="text-sm text-warning-text"
                  >
                    {editor.errores[i][field]}
                  </p>
                ),
            )}
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={añadirFila}
        disabled={!canWrite || guardando || curvaQ.isLoading}
        className="mb-4 w-full rounded-lg border border-dashed border-border py-2 text-sm text-muted-foreground"
      >
        {t('pesajes.curva.añadirPunto')}
      </button>

      <button
        type="button"
        onClick={onGuardar}
        disabled={!canWrite || guardando || curvaQ.isLoading}
        className="w-full rounded-lg bg-accent px-4 py-3 font-medium text-accent-contrast disabled:opacity-50"
      >
        {guardando ? t('pesajes.curva.guardando') : t('pesajes.curva.guardar')}
      </button>
      {editor.dialog}
    </main>
  );
}
